import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { getUrlForImage } from "../cubeComponent/constants/globalFunctions";
import { CONSTANTS } from "../cubeComponent/constants/globalVariable";
import axiosWrapper from "../services/axiosWrapper";
import { setOrganisationDetails } from "../reducers/organisationDetails";
import { portalOrganisationsUrl } from "@Services/dataManager";
import type {
  GetOrganisationDetailsRequest,
  UpdateOrganisationDetailsRequest,
  OrganisationsControllerRequest,
  GetOrganisationDetailsExtendedResponse,
  UpdateOrganisationDetailsResponse,
  OrganisationsControllerResponse,
  OrganisationDocument,
} from "@ServerTypes/organisations/organisations.schema";
import type { Exact } from "@Helpers/types/exact";

export type { OrganisationDocument };

export const getOrganisationData = async <T extends Exact<GetOrganisationDetailsRequest, T>>(data: T) => {
  return await axiosWrapper.post<GetOrganisationDetailsExtendedResponse>(
    `${getBackendUrl()}/organisations/getOrganisationDetails`,
    data,
  );
};

export function getOrganisationDetails<T extends Exact<GetOrganisationDetailsRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await getOrganisationData(data);
    // SCHEMA GAP: getOrganisationDetailsResponse is the opaque status envelope and does not
    // declare the `data` payload the handler really returns; only `_id` is read off it here.
    const organisationData = (response.data as typeof response.data & { data: { _id?: string } }).data;
    dispatch(
      setOrganisationDetails({
        ...organisationData,
        logoUrl: getUrlForImage({ id: organisationData._id }, CONSTANTS.ORGANIZATION),
      }),
    );
  };
}

export async function updateOrganisationDetails<T extends Exact<UpdateOrganisationDetailsRequest, T>>(data: T) {
  return await axiosWrapper.post<UpdateOrganisationDetailsResponse>(
    `${getBackendUrl()}/organisations/updateOrganisationDetails`,
    data,
  );
}

// Dispatch to the dedicated per-action organisation endpoints (split from the old organisationsController dispatcher).
const ORGANISATION_ACTION_PATHS: Record<string, string> = {
  insert: `${getBackendUrl()}/organisations/insertOrganisation`,
  update: `${getBackendUrl()}/organisations/updateOrganisation`,
  delete: `${getBackendUrl()}/organisations/deleteOrganisation`,
};

export const portalOrganisationsController = async <T extends Exact<OrganisationsControllerRequest, T>>(payload: T) => {
  const action = (payload as OrganisationsControllerRequest & { action?: string }).action;
  const url = (action && ORGANISATION_ACTION_PATHS[action]) || portalOrganisationsUrl;
  return await axiosWrapper.post<OrganisationsControllerResponse>(url, payload);
};
